import {useState, useCallback, useRef, useEffect, useMemo} from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {IconStatus} from 'shells/icon-status/icon-status';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {SetupContainer} from 'shells/setup/setup-container/setup-container';
import {Table} from 'shells/table/table';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';
import {Body} from 'shells/typography/body';

import {SettingsSidebar} from '../../../setup/settings-sidebar';

import {InboundSettings} from '../../inbound/inbound-settings';

import {useGetInboundEmailRules} from '../graphql/hooks/use-get-inbound-email-rules';
import {useGetSharedInboxConfig} from '../graphql/hooks/use-get-shared-inbox-config';
import {useUpdateInboundEmailRules} from '../graphql/hooks/use-update-inbound-email-rules';

import {AddSharedEmailAddressModal} from './add-shared-email-address-modal';
import {EmailAddressSettings} from './address-setup/email-address-settings';
import {getIsReplyingVerified} from './address-setup/helpers';
import {getSharedInboxRowStatus} from './helpers';
import {EmailSettingsSetup} from './onboarding/email-settings-setup';
import {SharedAddressRowActionMenu} from './shared-address-row-action-menu';

import styles from './settings.module.css';

const ENTRI_POLL_DURATION_MS = 10000;
const ENTRI_POLL_INTERVAL_MS = 2000;

interface Props {
    addressId?: string;
}

export type EmailAddressRow = {
    id: string;
    senderName: string;
    rawSenderName: string;
    emailAddress: string;
    statusAndConfigure: React.ReactNode;
};

export function Settings(props: Props) {
    const [shouldPollAfterEntri, setShouldPollAfterEntri] = useState<boolean>(false);
    const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const {inboundEmailRules, isLoading: isLoadingRules, refetch} = useGetInboundEmailRules();
    const {updateInboundEmailRules} = useUpdateInboundEmailRules();
    const {sharedInboxConfig, isLoading: isLoadingSharedInboxConfig} = useGetSharedInboxConfig({
        pollInterval: shouldPollAfterEntri ? ENTRI_POLL_INTERVAL_MS : 0,
    });

    const sharedAddresses = sharedInboxConfig?.sharedAddresses ?? [];

    useEffect(() => {
        return () => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
            }
        };
    }, []);

    const handleEntriComplete = useCallback(() => {
        setShouldPollAfterEntri(true);

        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
        }

        pollTimeoutRef.current = setTimeout(() => {
            setShouldPollAfterEntri(false);
            pollTimeoutRef.current = null;
        }, ENTRI_POLL_DURATION_MS);
    }, []);

    const emailAddressColumns = useMemo(
        () => [
            {
                id: 'senderName',
                Header: 'Sender name',
                accessor: (row: EmailAddressRow) => row.senderName,
                dataType: 'component' as const,
                Cell: ({cell}: {cell: {value: string}}) => <Body size='small'>{cell.value}</Body>,
            },
            {
                id: 'emailAddress',
                Header: 'Email address',
                accessor: (row: EmailAddressRow) => row.emailAddress,
                dataType: 'component' as const,
                Cell: ({cell}: {cell: {value: string}}) => <Body size='small'>{cell.value}</Body>,
            },
            {
                id: 'statusAndConfigure',
                Header: 'Status',
                accessor: (row: EmailAddressRow) => row.statusAndConfigure,
                dataType: 'component' as const,
                Cell: ({cell}: {cell: {value: React.ReactNode}}) => cell.value,
            },
        ],
        []
    );

    if (isLoadingRules || !inboundEmailRules || isLoadingSharedInboxConfig || !sharedInboxConfig) {
        return null;
    }

    if (props.addressId) {
        const selectedAddress = sharedAddresses.find((address) => address.id === props.addressId);

        return (
            <EmailAddressSettings
                sharedInboxConfig={sharedInboxConfig}
                sharedAddress={selectedAddress ?? null}
                onEntriComplete={handleEntriComplete}
            />
        );
    }

    if (sharedAddresses.length === 0 && !isAddingNew) {
        return <EmailSettingsSetup />;
    }

    const tableData: EmailAddressRow[] = sharedAddresses.map((address) => {
        const hasEmailAddress = Boolean(address.emailAddress.address);
        const isForwardingVerified = Boolean(address.forwardingVerifiedTime);
        const isReplyingVerified = getIsReplyingVerified(address.fromDomain);
        const status = getSharedInboxRowStatus(
            hasEmailAddress,
            isForwardingVerified,
            isReplyingVerified
        );

        return {
            id: address.id,
            senderName:
                address.emailAddress.display?.trim() ||
                address.emailAddress.formatted ||
                address.emailAddress.address ||
                '',
            rawSenderName: address.emailAddress.display?.trim() ?? '',
            emailAddress: address.emailAddress.address,
            statusAndConfigure: (
                <Stack isHorizontal={true} alignItems='center' spacing={24}>
                    <IconStatus level={status.level} text={status.text} />
                    <Link
                        // @ts-expect-error
                        as={RouterLink}
                        variant='primary'
                        size='small'
                        to={`/inbox/settings/email/${address.id}`}
                    >
                        Configure
                    </Link>
                </Stack>
            ),
        };
    });

    const firstRow = (
        <AddButtonRow
            buttonText='Add new address'
            isDisabled={isAddingNew}
            onClick={() => setIsAddingNew(true)}
        />
    );

    return (
        <div className={styles.container}>
            <Stack spacing={16}>
                <SetupContainer
                    header='Email addresses'
                    subtitle='The addresses you want to use for forwarding and replying to emails.'
                    noContentPadding={true}
                >
                    <Table
                        firstRow={firstRow}
                        columns={emailAddressColumns}
                        data={tableData}
                        disableFilters={true}
                        disableSorting={true}
                        fullWidth={true}
                        hasHeaders={true}
                        renderRowActionMenu={(row) => (
                            <SharedAddressRowActionMenu address={row.original} />
                        )}
                        isStriped={true}
                        headerBackgroundColor='offWhite'
                    />
                </SetupContainer>
                <InboundSettings
                    inboundRules={inboundEmailRules}
                    updateInboundRules={updateInboundEmailRules}
                    refetch={refetch}
                    isEnrolled={true}
                    threadType='EMAIL'
                    hasAILeadCreation={true}
                    hasNewColumnLayout={true}
                />
            </Stack>
            <AddSharedEmailAddressModal
                isOpen={isAddingNew}
                onClose={() => setIsAddingNew(false)}
            />
            <SettingsSidebar glossaryKey='/inbox/settings/email' />
        </div>
    );
}
